import React from 'react';
import styled from 'styled-components';
import { panel, panelMonoFont } from './panelTheme';
import { PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import { ZTooltip } from 'src/components/widgets/ztooltip';

// LE LETTERE DI UN ASSE — «Invert · X Y», le R G B di una mappa ORM, il «Gray · R» del canale di
// una texture.
//
// UNA FORMA PER UNA DOMANDA SOLA, «quale canale», e due modi di rispondere. Dove le lettere sono
// indipendenti — un asse si ribalta senza l'altro, succede ogni volta che una normale arriva da un
// motore che legge il verde al contrario — ognuna è premuta o no per conto suo (`aria-pressed`).
// Dove si escludono — una mappa si legge da un canale solo — sono una scelta (`exclusive`:
// `radiogroup` e `aria-checked`), e premere quella accesa non la spegne.
//
// LA LETTERA PORTA IL COLORE DEL SUO ASSE, lo stesso dei gizmo e dei campi numerici: una
// convenzione che in casa c'è già non si ridipinge. SENZA ASSE IL TONO È NEUTRO: «Gray» non è un
// canale ma tutti e tre, e un colore lo farebbe somigliare a uno dei tre. ACCESA È PIENA, SPENTA È
// CONTORNO — la differenza si legge anche senza distinguere i colori.
//
// Due spunte con la frase intera, «Invert normal map X» e «… Y», dicevano due volte metà della
// stessa cosa e si prendevano una riga ciascuna.

export type PropertiesPanelAxisToggle = {
    id: string;
    /** Il colore della lettera. Senza, il tono è neutro. */
    axis?: 'x' | 'y' | 'z';
    /** La lettera: di solito l'asse, ma sui canali di una texture gli stessi tre colori si chiamano R, G e B. */
    letter: string;
    pressed: boolean;
    ariaLabel: string;
    onChange?: (pressed: boolean) => void;
    disabled?: boolean;
    /** Perché questa lettera non si può scegliere. C'è, si vede e non si preme: il motivo compare passandoci sopra. */
    reason?: string;
};

export type PropertiesPanelAxisTogglesPropertyProps = {
    id: string;
    label: React.ReactNode;
    toggles: PropertiesPanelAxisToggle[];
    /** Le lettere si escludono: una sola è accesa. */
    exclusive?: boolean;
    disabled?: boolean;
};

const axisColor = { x: panel.axisX, y: panel.axisY, z: panel.axisZ };

const Letters = styled.span`
    display: inline-flex;
    gap: 4px;
    margin-left: auto;
`;

// UNA PAROLA È LARGA 44 E NON QUANTO IL SUO TESTO: accanto a una lettera sola è l'altra metà di una
// scelta, e uno scomparto che misura il proprio nome direbbe che le due metà pesano diverso.
const Letter = styled.button<{ $axis?: 'x' | 'y' | 'z'; $wide: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: ${(props) => (props.$wide ? '44px' : '18px')};
    height: 18px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    box-shadow: inset 0 0 0 1px ${panel.line};
    color: ${panel.inkMuted};
    font-family: ${panelMonoFont};
    font-size: 9.5px;
    font-weight: 700;
    cursor: pointer;
    transition:
        background 140ms ease,
        color 140ms ease,
        box-shadow 140ms ease;

    &[aria-pressed='false']:hover:not(:disabled):not([aria-disabled='true']),
    &[aria-checked='false']:hover:not(:disabled):not([aria-disabled='true']) {
        color: ${panel.ink};
    }

    /* Il pieno e il bordo si tingono da soli: currentColor è già il colore dell'asse. */
    &[aria-pressed='true'],
    &[aria-checked='true'] {
        color: ${(props) => (props.$axis ? axisColor[props.$axis] : panel.ink)};
        background: color-mix(in srgb, currentColor 16%, transparent);
        box-shadow: inset 0 0 0 1px color-mix(in srgb, currentColor 45%, transparent);
    }

    &:focus-visible {
        outline: 2px solid ${panel.accent};
        outline-offset: 1px;
    }

    &:disabled {
        cursor: default;
    }

    /* NON SI PUÒ SCEGLIERE, E DICE PERCHÉ. È \`aria-disabled\` e non \`disabled\`: un pulsante spento
       non riceve il puntatore, e il suo motivo non comparirebbe mai. */
    &[aria-disabled='true'] {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

/**
 * Una lettera sola. La monta la riga delle lettere, e la riga di un valore che legge da un canale —
 * vedi il cursore.
 */
export const PropertiesPanelAxisLetter: React.FC<{ toggle: PropertiesPanelAxisToggle; exclusive?: boolean; disabled?: boolean }> = ({
    toggle,
    exclusive,
    disabled
}) => (
    <ZTooltip title={toggle.reason} position='top'>
        <Letter
            type='button'
            $axis={toggle.axis}
            $wide={toggle.letter.length > 1}
            role={exclusive ? 'radio' : undefined}
            aria-checked={exclusive ? toggle.pressed : undefined}
            aria-pressed={exclusive ? undefined : toggle.pressed}
            aria-label={toggle.ariaLabel}
            aria-disabled={toggle.reason ? true : undefined}
            disabled={disabled || toggle.disabled}
            onClick={() => {
                if (toggle.reason || (exclusive && toggle.pressed)) return;
                toggle.onChange?.(!toggle.pressed);
            }}
        >
            {toggle.letter}
        </Letter>
    </ZTooltip>
);

export const PropertiesPanelAxisTogglesProperty: React.FC<PropertiesPanelAxisTogglesPropertyProps> = ({ id, label, toggles, exclusive, disabled }) => (
    <PropertiesPanelProperty disabled={disabled} lone>
        <PropertiesPanelLabel id={`${id}-label`}>{label}</PropertiesPanelLabel>
        <PropertiesPanelControl>
            <Letters role={exclusive ? 'radiogroup' : 'group'} aria-labelledby={`${id}-label`}>
                {toggles.map((toggle) => (
                    <PropertiesPanelAxisLetter key={toggle.id} toggle={toggle} exclusive={exclusive} disabled={disabled} />
                ))}
            </Letters>
        </PropertiesPanelControl>
    </PropertiesPanelProperty>
);
